/**
* App Kit DOM utility functions: finding containers, preventing multiple mounts,
* waiting for the DOM to be ready.
*/
/** Marks a container: `data-lmu-app="lmucast-playlist"`. */
export const APP_ATTRIBUTE = 'data-lmu-app';
/**
* State of a container: `data-lmu-app-state="mounted"` or `"failed"`.
* Set by the App Kit; do not write into the markup manually.
*/
export const STATE_ATTRIBUTE = 'data-lmu-app-state';
/**
* Finds all containers for an app.
*
* 1. New method: all elements with `data-lmu-app="<name>"` (multiple instances allowed).
* 2. Fallback for legacy markup: the element with the ID `containerId`.
*
* An element found via both methods is included only once.
*/
export function findContainers(name, containerId) {
    const containers = [];
    document
        .querySelectorAll(`[${APP_ATTRIBUTE}="${name}"]`)
        .forEach(el => containers.push(el));
    if (containerId) {
        const fallback = document.getElementById(containerId);
        if (fallback && containers.indexOf(fallback) === -1) {
            containers.push(fallback);
        }
    }
    return containers;
}
/**
* Has this element already been processed (successfully or with an error)?
* Prevents an app from being mounted into the same container a second time.
*/
export function isHandled(el) {
    return el.hasAttribute(STATE_ATTRIBUTE);
}
/** Marks the element as mounted. */
export function markAsMounted(el) {
    el.setAttribute(STATE_ATTRIBUTE, 'mounted');
}
/** Marks the element as failed – clearly visible when debugging in the DOM. */
export function markAsFailed(el) {
    el.setAttribute(STATE_ATTRIBUTE, 'failed');
}
/**
* Executes `callback` as soon as the DOM is ready for use.
* If the DOM is already ready (e.g., bundle at the end of the body or using `defer`),
* this happens immediately and synchronously—avoiding unnecessary event listeners.
*/
export function onReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => callback(), { once: true });
        return;
    }
    callback();
}
